
/*
	the way channelIds are constructed depends on the type of channel. 
	dm (between two users): `uu<user1Id>+<user2Id>`, the userIds are sorted alphabetically. 
	bot (between a user and a bot): `ub<userId>+<botId>` or `ub<botId>+<userId>`, 
		depending on alphabetic sort order of userId and botId. 
	group: `gr<groupId>`
	public channel: `pu<publicChannelId>`
	announcement (readonly public): `an<announcementChannelId>`
*/

import { MembershipStatus } from "./user.js"

let ChannelTypeValues = {
	// 1:1 between two users
	uu: 'uu-',  
	dm: 'uu-', 

	// 1:1 between a user and a bot
	ub: 'ub-',  
	bot: 'ub-', 

	// hangaround group
	hg: 'hg-',  
	hangaroundGroup: 'hg-', 

	// user-created group (not yet implemented)
	ug: 'ug-',  
	userGroup: 'ug-', 

	// public chat
	pu: 'pu-',  
	public: 'pu-', 

	// announcement
	an: 'an-',  
	announcement: 'an-', 

	// support
	su: 'su-',  
	support: 'su-', 
} as const

export type ChannelType = typeof ChannelTypeValues[keyof typeof ChannelTypeValues]

export const ChannelType = { 
	...ChannelTypeValues, 
	fromChannelId: (channelId: string): ChannelType | undefined => { 
		return ChannelTypeValues[channelId.slice(0, 2) as keyof typeof ChannelTypeValues]
	}
}

export interface ChannelData {
	id: string
	readableId: string
	name: string
	details?: ChannelDetails
	hasUnreadMessages?: boolean
	myMembershipStatus?: MembershipStatus
	dmStatus?: {
		user1Id: string
		user2Id: string
		user1Name?: string
		user2Name?: string
		user1Status: 'pending' | 'accepted' | 'rejected'
		user2Status: 'pending' | 'accepted' | 'rejected'
	}
}

interface DmChannelStatus {
	user1Id: string
	user2Id: string
	user1Status: 'pending' | 'accepted' | 'rejected'
	user2Status: 'pending' | 'accepted' | 'rejected'
	myStatus?: 'pending' | 'accepted' | 'rejected'
	theirStatus?: 'pending' | 'accepted' | 'rejected'
	theirUserId?: string
	theirName?: string
}

export interface Channel extends ChannelData {
	readonly type: ChannelType
	readonly typeSymbol: string
	readonly displayId: string

	readonly isReadOnly: boolean
	readonly isLimitedMembership: boolean
	readonly showsUserList: boolean
	readonly usersCanLeave: boolean
	readonly usersCanReact: boolean
	readonly usersCanSendImages: boolean

	readonly dmStatus?: DmChannelStatus
}

export interface ChannelDetails {
	description?: string
	membersByUserId: Record<string, ChannelMember>
}

export class ChannelImpl implements Channel {
	id: string
	readableId: string
	name: string
	details?: ChannelDetails
	hasUnreadMessages?: boolean
	myMembershipStatus?: MembershipStatus

	readonly type: ChannelType
	readonly typeSymbol: string
	readonly displayId: string
	readonly dmStatus?: DmChannelStatus

	readonly isReadOnly: boolean
	readonly isLimitedMembership: boolean
	readonly showsUserList: boolean
	readonly usersCanLeave: boolean
	readonly usersCanReact: boolean
	readonly usersCanSendImages: boolean

	constructor(data: ChannelData, myUserId?: string) {
		this.id = data.id
		this.readableId = data.readableId
		this.name = data.name
		this.details = data.details
		this.hasUnreadMessages = data.hasUnreadMessages
		this.myMembershipStatus = data.myMembershipStatus

		const type = ChannelType.fromChannelId(this.id)
		if (!type)
			throw new Error(`Invalid channelId: ${this.id}`)
		this.type = type

		if (data.dmStatus && myUserId) {
			const isUser1 = myUserId === data.dmStatus.user1Id
			this.dmStatus = {
				...data.dmStatus,
				myStatus: isUser1 ? data.dmStatus.user1Status : data.dmStatus.user2Status,
				theirStatus: isUser1 ? data.dmStatus.user2Status : data.dmStatus.user1Status,
				theirUserId: isUser1 ? data.dmStatus.user2Id : data.dmStatus.user1Id,
				theirName: isUser1 ? data.dmStatus.user2Name : data.dmStatus.user1Name
			}
		}

		switch (this.type) {
			case ChannelType.dm:
				this.typeSymbol = '👤'
				break
			case ChannelType.bot:
				this.typeSymbol = '🤖'
				break
			case ChannelType.hangaroundGroup:
			case ChannelType.userGroup:
				this.typeSymbol = '👥'
				break
			case ChannelType.public:
				this.typeSymbol = '🌍'
				break
			case ChannelType.announcement:
				this.typeSymbol = '📢'
				break
			case ChannelType.support:
				this.typeSymbol = '🛟'
				break
			default:
				this.typeSymbol = '💬'
				break
		}

		if (this.type === ChannelType.dm) {
			const theirUserId = this.dmStatus?.theirUserId

			if (theirUserId) { 
				const theirName = this.dmStatus?.theirName
				if (theirName) { 
					this.displayId = `${this.typeSymbol} ${theirName} (${theirUserId})`
				} else { 
					const theirName = Object.values(this.details?.membersByUserId ?? {})
						.find(m => m.userId === theirUserId)?.name || theirUserId
					this.displayId = `${this.typeSymbol} ${theirName} (${theirUserId})`
				}
			} else { 
				this.displayId = `${this.typeSymbol} ${this.readableId}`
			}
		}
		else
			this.displayId = `${this.typeSymbol} ${this.readableId}`

		this.showsUserList = this.type === ChannelType.hangaroundGroup || this.type === ChannelType.userGroup
		this.usersCanLeave = this.type === ChannelType.hangaroundGroup || this.type === ChannelType.userGroup
		this.usersCanReact = this.type !== ChannelType.bot
		this.usersCanSendImages = this.type !== ChannelType.bot

		this.isLimitedMembership = this.myMembershipStatus === 'limited'

		this.isReadOnly = false
		if (this.type === ChannelType.announcement)
			this.isReadOnly = true
		if (this.type === ChannelType.dm) {
			if (this.dmStatus?.myStatus !== 'accepted'
				|| this.dmStatus?.theirStatus !== 'accepted')
				this.isReadOnly = true
		}
		if (this.isLimitedMembership)
			this.isReadOnly = true
	}
}

export interface ChannelMember {
	userId: string
	name?: string
	color?: string
	includeUserIdInSender: boolean
	membershipStatus?: MembershipStatus
}

export function getChannelId(params: 
	| { userId1: string, userId2: string } 
	| { userId: string, botId: string }
	| { groupId: string }
	| { publicChannelId: string }
	| { announcementChannelId: string }
	| { supportUserId: string }
): string { 
	if ('userId1' in params && 'userId2' in params) {
		return (params.userId1 < params.userId2) ? 
			`${ChannelType.dm}${params.userId1}+${params.userId2}` : 
			`${ChannelType.dm}${params.userId2}+${params.userId1}`
	}
	if ('userId' in params && 'botId' in params) {
		return `${ChannelType.bot}${params.userId}+${params.botId}`
	}
	if ('groupId' in params) {
		return `${ChannelType.hangaroundGroup}${params.groupId}`
	}
	if ('publicChannelId' in params) {
		return `${ChannelType.public}${params.publicChannelId}`
	}
	if ('announcementChannelId' in params) {
		return `${ChannelType.announcement}${params.announcementChannelId}`
	}
	if ('supportUserId' in params) {
		return `${ChannelType.support}${params.supportUserId}`
	}
	throw new Error('Invalid channel params')
}
